import { 
  studyNormalize, 
  downloadNormalizedStudy,
  NormalizeResponse 
} from '../../../utils/normalizeStudy';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '../../../components/ui/button';
import { useState } from 'react';
import { Loader2, CheckCircle, Download, AlertCircle } from 'lucide-react';

interface NormalizeButtonProps {
  studyInstanceUID: string;
  studyDescription?: string;
}

type NormalizeStatus = 
  | 'idle' 
  | 'normalizing' 
  | 'downloading' 
  | 'complete' 
  | 'error';

export const NormalizeButton: React.FC<NormalizeButtonProps> = ({
  studyInstanceUID,
  studyDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [status, setStatus] = useState<NormalizeStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [normalizeResult, setNormalizeResult] = useState<NormalizeResponse | null>(null);

  const handleNormalize = async () => {
    setStatus('normalizing');
    setErrorMessage('');
    
    try {
      const authToken = await getAccessTokenSilently();
      
      // Step 1: Normalize the study
      console.log('Starting normalization...');
      const result = await studyNormalize(authToken, studyInstanceUID);
      setNormalizeResult(result);
      
      if (result.status === 'success' || result.total_datasets_uploaded > 0) {
        console.log('Normalization successful:', result);
        
        // Step 2: Wait a moment for DICOM store to index (3 seconds)
        await new Promise(resolve => setTimeout(resolve, 10000));
        
        // Step 3: Download the normalized study
        setStatus('downloading');
        console.log('Downloading normalized study...');
        
        try {
          await downloadNormalizedStudy(authToken, studyInstanceUID, studyDescription);
          
          setStatus('complete');
          console.log('Process complete!');
          
          // Reset to idle after showing success
          setTimeout(() => {
            setStatus('idle');
            setNormalizeResult(null);
          }, 5000);
        } catch (downloadError) {
          console.error('Error downloading normalized study:', downloadError);
          setErrorMessage(
            downloadError instanceof Error 
              ? `Download failed: ${downloadError.message}` 
              : 'Failed to download normalized study'
          );
          setStatus('error');
          
          // Auto-reset error after 8 seconds
          setTimeout(() => {
            setStatus('idle');
            setErrorMessage('');
          }, 8000);
        }
      } else {
        throw new Error(result.message || 'Normalization failed');
      }
    } catch (error) {
      console.error('Error during normalization:', error);
      setErrorMessage(
        error instanceof Error 
          ? `Normalization failed: ${error.message}` 
          : 'Normalization failed'
      );
      setStatus('error');
      
      // Auto-reset error after 8 seconds
      setTimeout(() => {
        setStatus('idle');
        setErrorMessage('');
      }, 8000);
    }
  };

  const getButtonContent = () => {
    switch (status) {
      case 'normalizing':
        return (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        );
      case 'downloading':
        return (
          <>
            <Download className="w-4 h-4 mr-1 animate-bounce" />
            Downloading...
          </>
        );
      case 'complete':
        return (
          <>
            <CheckCircle className="w-4 h-4 mr-1" />
            Complete!
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle className="w-4 h-4 mr-1" />
            Failed
          </>
        );
      default:
        return 'Normalize';
    }
  };

  const getButtonClassName = () => {
    const baseClass = "flex w-auto transition-colors rounded";
    
    switch (status) {
      case 'downloading':
        return `${baseClass} bg-blue-600 text-white hover:bg-blue-700`;
      case 'complete':
        return `${baseClass} bg-green-600 text-white`;
      case 'error':
        return `${baseClass} bg-red-600 text-white hover:bg-red-700`;
      default:
        return `${baseClass} hover:bg-green-600 hover:text-white`;
    }
  };

  const isDisabled = status !== 'idle' && status !== 'error';

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="outline"
        className={getButtonClassName()}
        onClick={handleNormalize}
        disabled={isDisabled}
      >
        {getButtonContent()}
      </Button>
      
      {errorMessage && (
        <span className="text-xs text-red-600 max-w-xs">
          {errorMessage}
        </span>
      )}
      
      {normalizeResult && status === 'downloading' && (
        <span className="text-xs text-blue-600">
          {normalizeResult.total_datasets_uploaded} series normalized, downloading...
        </span>
      )}
      
      {normalizeResult && status === 'complete' && (
        <span className="text-xs text-green-600">
          ✓ Downloaded {normalizeResult.total_datasets_uploaded} series
        </span>
      )}
    </div>
  );
};